import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import {
  applyDecorator,
  getAvailableDecorators,
} from "@/components/doc/right-sidebar/active-node-style/variable/decorators";
import { hasDecorator } from "@/components/expression/utils/decorators/utils";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown } from "@/components/sidebar/Dropdown";

import type { DecoratorTypeValue } from "@/components/suggestions/providers/data/types";
import type { NodeModel } from "@/models/nodes/types";

type Props = {
  node: NodeModel;
  decoratorTypes?: DecoratorTypeValue[];
  tooltipTitle?: string;
  className?: string;
};

export const DecoratorsMenu = observer(function DecoratorsMenu({
  node,
  decoratorTypes,
  tooltipTitle,
  className,
}: Props) {
  const t = useTranslations();
  const availableDecorators = getAvailableDecorators(node, decoratorTypes);

  if (availableDecorators.length === 0) {
    return null;
  }

  const items = availableDecorators.map((decorator) => ({
    title: decorator.intlName ? t(`suggestions.${decorator.intlName}`) : "Decorator",
    icon: decorator.icon ? <Icon name={decorator.icon} /> : null,
    active: hasDecorator(node, decorator.decoratorType),
    onClick: () => applyDecorator(node, decorator.decoratorType),
  }));

  const activeItem = items.find((item) => item.active);
  const [firstItem] = items;
  const defaultIcon = decoratorTypes ? firstItem?.icon : null;

  return (
    <Dropdown
      className={className}
      toggleButtonComponent={
        <Sidebar.ToggleButton
          as="div"
          icon={activeItem?.icon ?? defaultIcon ?? <Icon name="math/decorator/Decorator" />}
          isActive={!!activeItem}
          tooltipTitle={tooltipTitle ?? t("suggestions.decorator.applyDecorator")}
          className="min-h-full"
        />
      }
      items={items}
    />
  );
});
